import { createContext, useContext } from 'react';

/**
 * The fixed **width** every slide is authored against. Slides are a landscape
 * (16:9) surface 1280px wide and at least 720px tall — rendered at this native
 * width and `transform: scale()`d to the available width, then allowed to grow
 * downward with their content (the parent scrolls). 1280 scales by ×1.5 to a
 * 1920×1080 classroom screen, so it stays pixel-crisp.
 *
 * This is the shared contract between the LMS authoring canvas, the teach
 * player, the preview, and the Electron classroom renderer:
 *   - freeform `position.{x,y,w,h}` are pixels in this 1280-wide design space
 *   - `heightPercent` is a % of 720, `widthPercent` a % of 1280
 *
 * Previously each surface used its own implicit max-width (760 / 820), which
 * produced the narrow/portrait look on landscape screens. Everything now flows
 * from this one constant.
 */
export const DESIGN_CANVAS = { width: 1280, height: 720 } as const;

/**
 * The scale factor currently applied to the design canvas by the nearest
 * `ScaledStage`. Defaults to `1` so any component rendered outside a scaled
 * stage behaves as if drawn at native size (its previous behavior).
 *
 * Only components doing pointer-delta math in design-space pixels (freeform
 * drag/resize) need to read this — everything else renders in pure design
 * coordinates and is scaled transparently by the CSS transform.
 */
export const CanvasScaleContext = createContext<number>(1);

export const useCanvasScale = () => useContext(CanvasScaleContext);
